import { retrieveLaunchParams } from "@telegram-apps/bridge"
import {
	backButton,
	closingBehavior,
	miniApp,
	postEvent,
	requestContentSafeAreaInsets,
	requestSafeAreaInsets,
	init as sdkInit,
	swipeBehavior,
	themeParams,
	viewport,
} from "@telegram-apps/sdk-react"

// Промис инициализации: повторный вызов возвращает его, не переинициализируя SDK.
let initPromise: Promise<void> | null = null

// Глобальные слушатели resyncViewport вешаем один раз.
let viewportListenersBound = false
let resyncViewportHandler: (() => void) | null = null

// Поколение жизненного цикла: dispose() инкрементит, init фиксирует значение в
// начале и не довешивает слушатели, если во время await случился HMR-dispose.
let lifecycleId = 0

function hasLaunchParams(): boolean {
	try {
		retrieveLaunchParams()
		return true
	} catch {
		return false
	}
}

export function initializeTelegramSDK(): Promise<void> {
	if (initPromise) {
		return initPromise
	}
	// На reject сбрасываем промис — чтобы повторный вызов мог ретраить, а не получать кэш ошибки.
	initPromise = doInitializeTelegramSDK().catch((error) => {
		initPromise = null
		throw error
	})
	return initPromise
}

async function doInitializeTelegramSDK(): Promise<void> {
	if (typeof window === "undefined") {
		return
	}

	// Фиксируем поколение этого запуска — сверим перед навешиванием слушателей.
	const myLifecycleId = lifecycleId

	try {
		try {
			sdkInit()
			console.log("[Telegram] SDK initialized successfully ✓")
		} catch (error) {
			let reason = "Unknown error"
			if (error instanceof Error) reason = error.message
			console.warn("[Telegram] SDK initialization warning:", reason)

			const isTelegramEnv = hasLaunchParams()

			if (!isTelegramEnv) {
				console.info(
					"[Telegram] Running outside Telegram environment (expected in dev/testing)",
				)
			} else {
				console.error(
					"[Telegram] Failed to initialize SDK in Telegram environment:",
					error,
				)
			}
		}

		// Монтируем компоненты: только если доступно и ещё не смонтировано (защита от повторного mount при HMR).
		if (backButton.mount.isAvailable() && !backButton.isMounted()) {
			backButton.mount()
		}

		if (closingBehavior.mount.isAvailable() && !closingBehavior.isMounted()) {
			closingBehavior.mount()
		}

		if (miniApp.mountSync.isAvailable() && !miniApp.isMounted()) {
			miniApp.mountSync()
		}

		// ready() как можно раньше после init(): до init() ifAvailable() молча
		// возвращает [false], а любая ошибка ниже увела бы нас в catch — и клиент
		// Telegram так и не узнал бы, что приложение готово.
		const [readySent] = miniApp.ready.ifAvailable()
		if (!readySent) {
			console.warn("[Telegram] miniApp.ready() unavailable after init")
		}

		if (themeParams.mountSync.isAvailable() && !themeParams.isMounted()) {
			themeParams.mountSync()
		}

		if (swipeBehavior.mount.isAvailable() && !swipeBehavior.isMounted()) {
			swipeBehavior.mount()
		}

		// Mount viewport — internally requests dimensions + safe-area insets
		if (viewport.mount.isAvailable() && !viewport.isMounted()) {
			try {
				await viewport.mount()
			} catch (error) {
				if (
					!(error instanceof Error) ||
					!error.message.includes("already mounting")
				) {
					throw error
				}
			}
		}

		// Request fullscreen mode
		if (viewport.requestFullscreen.isAvailable()) {
			try {
				await viewport.requestFullscreen()
			} catch (error) {
				console.warn("[Telegram] Fullscreen request failed:", error)
			}
		}

		if (viewport.isMounted()) {
			// Re-request safe-area insets: mount() fetched pre-fullscreen values, but
			// entering fullscreen changes which areas overlap content.
			try {
				const requests: Promise<unknown>[] = []
				if (requestSafeAreaInsets.isAvailable()) {
					requests.push(requestSafeAreaInsets())
				}
				if (requestContentSafeAreaInsets.isAvailable()) {
					requests.push(requestContentSafeAreaInsets())
				}
				if (requests.length > 0) {
					await Promise.all(requests)
				}
			} catch (error) {
				console.warn("[Telegram] Safe area re-request failed:", error)
			}

			// Bind viewport CSS vars (dimensions + safe area). SDK auto-updates them on signal changes.
			try {
				if (viewport.bindCssVars.isAvailable() && !viewport.isCssVarsBound()) {
					viewport.bindCssVars()
				}
			} catch (error) {
				console.warn("[Telegram] viewport CSS vars binding failed:", error)
			}
		}

		// Bind theme color CSS vars
		try {
			if (miniApp.bindCssVars.isAvailable()) {
				miniApp.bindCssVars()
			}
		} catch (error) {
			console.warn("miniApp CSS vars already bound", error)
		}

		try {
			if (themeParams.bindCssVars.isAvailable()) {
				themeParams.bindCssVars()
			}
		} catch (error) {
			console.warn("themeParams CSS vars already bound", error)
		}

		// Disable vertical swipes to prevent accidental closure
		if (swipeBehavior.disableVertical.isAvailable()) {
			swipeBehavior.disableVertical()
		}

		// Lock orientation
		try {
			postEvent("web_app_toggle_orientation_lock", { locked: true })
		} catch (error) {
			console.warn("[Telegram] Failed to lock orientation:", error)
		}

		// Re-sync on return to Mini App: Telegram doesn't always re-emit viewport_changed,
		// so stale height/safe-area insets push bottom buttons under the native overlay.
		const resyncViewport = async () => {
			if (document.visibilityState !== "visible") return

			// Guard against ConcurrentCallError: on resume `focus` + `visibilitychange`
			// fire together, racing two requestFullscreen() calls. Isolated so its
			// failure doesn't skip the safe-area refresh below.
			if (
				viewport.requestFullscreen.isAvailable() &&
				!viewport.changeFullscreenPromise()
			) {
				try {
					await viewport.requestFullscreen()
				} catch (error) {
					console.warn("[Telegram] Fullscreen re-request failed:", error)
				}
			}

			try {
				const requests: Promise<unknown>[] = []
				if (requestSafeAreaInsets.isAvailable()) {
					requests.push(requestSafeAreaInsets())
				}
				if (requestContentSafeAreaInsets.isAvailable()) {
					requests.push(requestContentSafeAreaInsets())
				}
				if (requests.length > 0) {
					await Promise.all(requests)
				}
				if (viewport.bindCssVars.isAvailable() && !viewport.isCssVarsBound()) {
					viewport.bindCssVars()
				}
			} catch (error) {
				console.warn("[Telegram] Viewport re-sync failed:", error)
			}
		}

		// dispose (HMR) во время init — не довешиваем слушатели (ссылка осталась бы в устаревшем модуле = утечка).
		if (lifecycleId !== myLifecycleId) {
			return
		}

		// Слушатели вешаем один раз — даже при повторном init.
		if (!viewportListenersBound) {
			resyncViewportHandler = () => {
				void resyncViewport()
			}
			document.addEventListener("visibilitychange", resyncViewportHandler)
			window.addEventListener("focus", resyncViewportHandler)
			viewportListenersBound = true
		}
	} catch (error) {
		console.error("Failed to initialize Telegram SDK:", error)
		throw error
	}
}

// Снимает слушатели и сбрасывает идемпотентные флаги (HMR-teardown, чтобы не плодить дубли).
export function disposeTelegramSDK(): void {
	if (resyncViewportHandler) {
		document.removeEventListener("visibilitychange", resyncViewportHandler)
		window.removeEventListener("focus", resyncViewportHandler)
		resyncViewportHandler = null
	}
	viewportListenersBound = false
	initPromise = null
	// Помечаем in-flight init устаревшим, чтобы не довесил слушатели.
	lifecycleId++
}
